import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

/**
 * The checks on an office's own visit reminder SMS (DEV-3553, Jonas
 * 2026-10-28). The sender in rentio-api-serverless (visitReminder.sms.utils)
 * fills the same placeholders and sends its standard copy for a text that
 * fails them, so a rule changed here has to change there too.
 */

export const SMS_SEGMENT_LENGTH = 160;

/** What every text must say: a reminder without when or where is no reminder. */
export const REQUIRED_PLACEHOLDERS = ["address", "visitDate", "visitTime"];

export const OPTIONAL_PLACEHOLDERS = [
  "contactName",
  "contactEmail",
  "officePhone",
];

const KNOWN_PLACEHOLDERS = [...REQUIRED_PLACEHOLDERS, ...OPTIONAL_PLACEHOLDERS];

/**
 * One fictional visit, the same for every office and every language, so the
 * limit means the same thing everywhere. A long street on purpose: it leaves
 * our own default texts 8 to 17 characters of room (Jonas, 2026-10-28). Never
 * a real office's data. The values are shown in the preview, so they live in
 * the translations and are read by the screen.
 */
export const getSmsExampleValues = (): { [placeholder: string]: string } => ({
  address: getLocalizedText(
    "tenant_profile.notification.visit_reminder_sms.example.address",
  ),
  visitDate: "12/01",
  visitTime: "14:30",
  contactName: getLocalizedText(
    "tenant_profile.notification.visit_reminder_sms.example.contact_name",
  ),
  contactEmail: getLocalizedText(
    "tenant_profile.notification.visit_reminder_sms.example.contact_email",
  ),
  officePhone: getLocalizedText(
    "tenant_profile.notification.visit_reminder_sms.example.office_phone",
  ),
});

// Anything between double braces, so `{{visit_date}}` or `{{address2}}` is
// reported as unknown instead of slipping through as plain text and reaching
// the candidate raw. The known names are letters only, so they match the same.
/**
 * The text each box shows and Save writes: what the office typed in this
 * visit, otherwise what is stored now. Read on every render, so preferences
 * that arrive after the screen opened still reach the boxes the office has
 * not touched, instead of the defaults they started with being saved over
 * the office's own texts.
 */
export const getSmsTextByKey = (params: {
  stored: { key: string; value: unknown }[];
  edited: { [key: string]: string };
}): { [key: string]: string } => {
  const { stored, edited } = params;
  const result: { [key: string]: string } = {};
  stored.forEach(({ key, value }) => {
    result[key] = Object.prototype.hasOwnProperty.call(edited, key)
      ? edited[key]
      : `${value || ""}`;
  });
  return result;
};

const PLACEHOLDER = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Characters a word processor puts in, swapped for the plain ones while the
 * office types: one curly quote would make the whole SMS a long one, and
 * nobody can see the difference.
 */
const TYPOGRAPHIC_REPLACEMENTS: [RegExp, string][] = [
  [/[‘’‚′]/g, "'"],
  [/[“”„″]/g, '"'],
  [/[–—]/g, "-"],
  [/…/g, "..."],
  [/ /g, " "],
];

export const toPlainSmsCharacters = (text: string) =>
  TYPOGRAPHIC_REPLACEMENTS.reduce(
    (result, [pattern, plain]) => result.replace(pattern, plain),
    text,
  );

/** The GSM-7 alphabet: one character each, 160 to a segment. */
const GSM7_BASIC =
  // cspell:disable-next-line -- a character set, not words
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** GSM-7 too, but each costs two characters. */
const GSM7_EXTENSION = "^{}\\[~]|€";

/**
 * French letters outside GSM-7 that an office may still write (Jonas,
 * 2026-10-28). They make the SMS a long one, which the counter says.
 */
// cspell:disable-next-line -- a character set, not words
const ALLOWED_OUTSIDE_GSM7 = "êçâîôûëïÊÂÎÔÛËÏ";

export interface ISmsCheck {
  /** The text as the example candidate would read it. */
  preview: string;
  /** Characters of the preview, an extension character counting twice. */
  length: number;
  /** How many SMS parts the preview is sent as. */
  segments: number;
  /** The letters that make it a long SMS, when there are any. */
  longSmsCharacters: string[];
  missingPlaceholders: string[];
  unknownPlaceholders: string[];
  /** Characters no SMS can carry here, like emoji. */
  forbiddenCharacters: string[];
  canSave: boolean;
}

const unique = (values: string[]) => Array.from(new Set(values));

export const checkSms = (params: {
  text: string;
  exampleValues: { [placeholder: string]: string };
}): ISmsCheck => {
  const { text, exampleValues } = params;
  const used = Array.from(text.matchAll(PLACEHOLDER)).map(match => match[1]);
  const missingPlaceholders = REQUIRED_PLACEHOLDERS.filter(
    name => !used.includes(name),
  );
  const unknownPlaceholders = unique(
    used.filter(name => !KNOWN_PLACEHOLDERS.includes(name)),
  );

  const preview = text.replace(
    PLACEHOLDER,
    (match, name: string) => exampleValues[name] ?? match,
  );
  const characters = Array.from(preview);

  const forbiddenCharacters = unique(
    characters.filter(
      character =>
        !GSM7_BASIC.includes(character) &&
        !GSM7_EXTENSION.includes(character) &&
        !ALLOWED_OUTSIDE_GSM7.includes(character),
    ),
  );
  const longSmsCharacters = unique(
    characters.filter(character => ALLOWED_OUTSIDE_GSM7.includes(character)),
  );

  const isUnicode = longSmsCharacters.length > 0;
  const length = isUnicode
    ? characters.length
    : characters.reduce(
        (sum, character) => sum + (GSM7_EXTENSION.includes(character) ? 2 : 1),
        0,
      );

  // A message over one segment is split into parts that each lose a few
  // characters to the header that joins them back together. The office is
  // billed one SMS per part (DEV-3572): the billing worker counts the text it
  // sends with the same rule (countSmsParts in rentio-api-serverless).
  const single = isUnicode ? 70 : SMS_SEGMENT_LENGTH;
  const part = isUnicode ? 67 : 153;
  const segments = length <= single ? 1 : Math.ceil(length / part);

  return {
    preview,
    length,
    segments,
    longSmsCharacters,
    missingPlaceholders,
    unknownPlaceholders,
    forbiddenCharacters,
    canSave:
      text.trim().length > 0 &&
      length <= SMS_SEGMENT_LENGTH &&
      missingPlaceholders.length === 0 &&
      unknownPlaceholders.length === 0 &&
      forbiddenCharacters.length === 0,
  };
};
